'use client';

import { useCallback } from 'react';
import { captureException } from 'src/observability';
import type {
  EnrichedRouteStop,
  RouteDirections,
  RouteLeg,
  RouteStep,
  FetchDirectionsOptions,
  UseRouteDirectionsReturn,
} from './types';
import { chunkWaypoints } from './utils';

/**
 * Decode an encoded polyline string into an array of lat/lng points.
 * Mapbox uses the same Encoded Polyline Algorithm Format as Google.
 * @see https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
function decodePolyline(encoded: string): Array<{ lat: number; lng: number }> {
  const points: Array<{ lat: number; lng: number }> = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte: number;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += deltaLat;

    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += deltaLng;

    points.push({
      lat: lat / 1e5,
      lng: lng / 1e5,
    });
  }

  return points;
}

/**
 * Mapbox Directions API response types
 * @see https://docs.mapbox.com/api/navigation/directions/
 */
interface MapboxManeuver {
  instruction?: string;
  type?: string;
  modifier?: string;
}

interface MapboxStep {
  distance: number;
  duration: number;
  geometry?: string;
  maneuver?: MapboxManeuver;
  mode?: string;
}

interface MapboxLeg {
  distance: number;
  duration: number;
  steps: MapboxStep[];
  summary?: string;
}

interface MapboxRoute {
  distance: number;
  duration: number;
  geometry: string;
  legs: MapboxLeg[];
}

interface MapboxDirectionsResponse {
  code: string;
  routes?: MapboxRoute[];
  message?: string;
}

/** Windows requested at once, to stay under Mapbox's rate limit */
const WINDOW_CONCURRENCY = 4;
/** Successful windows kept so an edit only refetches the windows it changed */
const WINDOW_CACHE_LIMIT = 100;

type LatLng = { lat: number; lng: number };

interface WindowDirections {
  legs: RouteLeg[];
  polylinePoints: LatLng[];
  distanceMeters: number;
  durationSeconds: number;
  isApproximate: boolean;
}

/**
 * Keyed by travel profile and coordinates only, and holds public road geometry,
 * so nothing in it depends on the signed-in user and identity changes need not
 * clear it.
 */
const windowCache = new Map<string, WindowDirections>();

/** Empties the window cache; tests call it so one case's result can't leak into the next. */
export const clearDirectionsWindowCache = (): void => windowCache.clear();

const formatDistance = (meters: number): string => {
  const miles = meters / 1609.344;
  return miles < 1
    ? `${Math.round(meters * 3.28084)} ft`
    : `${miles.toFixed(1)} mi`;
};

const formatDuration = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
};

const getMapboxProfile = (travelMode: string): string =>
  travelMode === 'WALKING' || travelMode === 'WALK'
    ? 'walking'
    : travelMode === 'BICYCLING' || travelMode === 'BICYCLE'
      ? 'cycling'
      : 'driving';

/**
 * Straight-line stand-in for a window Mapbox couldn't route: one leg per stop
 * pair with no distance or duration, so totals only count real roads.
 */
const buildApproximateWindow = (stops: EnrichedRouteStop[]): WindowDirections => {
  const points = stops.map(({ lat, lng }) => ({ lat, lng }));
  const legs = points.slice(1).map(
    (end, index): RouteLeg => ({
      startAddress: '',
      endAddress: '',
      distanceMeters: 0,
      distanceText: formatDistance(0),
      durationSeconds: 0,
      durationText: formatDuration(0),
      polylinePoints: [points[index], end],
      steps: [],
      isApproximate: true,
    })
  );
  return {
    legs,
    polylinePoints: points,
    distanceMeters: 0,
    durationSeconds: 0,
    isApproximate: true,
  };
};

/**
 * Request one window of stops (at most DIRECTIONS_MAX_WAYPOINTS) from Mapbox.
 * Returns null when Mapbox can't route it or `signal` aborts the request.
 */
const fetchWindow = async (
  stops: EnrichedRouteStop[],
  profile: string,
  accessToken: string,
  signal?: AbortSignal
): Promise<WindowDirections | null> => {
  // Mapbox coordinates format: lng,lat;lng,lat;...
  const coordinates = stops.map((s) => `${s.lng},${s.lat}`).join(';');
  const cacheKey = `${profile}|${coordinates}`;
  const cached = windowCache.get(cacheKey);
  if (cached) return cached;

  const params = new URLSearchParams({
    access_token: accessToken,
    geometries: 'polyline',
    overview: 'full',
    steps: 'true',
  });

  try {
    const response = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}?${params}`,
      { signal }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '[useRouteDirections] Mapbox API error:',
        `status=${response.status}`,
        errorText
      );
      return null;
    }

    const data: MapboxDirectionsResponse = await response.json();

    if (data.code !== 'Ok' || !data.routes?.length) {
      console.error(
        '[useRouteDirections] Mapbox returned:',
        data.code,
        data.message
      );
      return null;
    }

    const route = data.routes[0];

    const legs: RouteLeg[] = route.legs.map((leg) => {
      // Build per-leg polyline from step geometries
      const legPoints: LatLng[] = [];
      for (const step of leg.steps || []) {
        if (step.geometry) {
          const stepPoints = decodePolyline(step.geometry);
          // Avoid duplicate points at step boundaries
          if (legPoints.length > 0 && stepPoints.length > 0) {
            stepPoints.shift();
          }
          legPoints.push(...stepPoints);
        }
      }

      return {
        startAddress: '',
        endAddress: '',
        distanceMeters: leg.distance,
        distanceText: formatDistance(leg.distance),
        durationSeconds: leg.duration,
        durationText: formatDuration(leg.duration),
        polylinePoints: legPoints,
        steps: (leg.steps || []).map(
          (step): RouteStep => ({
            instructions: step.maneuver?.instruction || '',
            distanceMeters: step.distance,
            distanceText: formatDistance(step.distance),
            durationSeconds: step.duration,
            durationText: formatDuration(step.duration),
            travelMode: step.mode || profile,
          })
        ),
      };
    });

    const result: WindowDirections = {
      legs,
      polylinePoints: decodePolyline(route.geometry),
      distanceMeters: route.distance,
      durationSeconds: route.duration,
      isApproximate: false,
    };

    if (windowCache.size >= WINDOW_CACHE_LIMIT) {
      const oldestKey = windowCache.keys().next().value;
      if (oldestKey !== undefined) windowCache.delete(oldestKey);
    }
    windowCache.set(cacheKey, result);
    return result;
  } catch (error) {
    // An abort is the caller moving on, not a failure
    if (signal?.aborted) return null;
    console.error('[useRouteDirections] Error fetching directions:', error);
    captureException(error, { tags: { context: 'route-directions-window' } });
    return null;
  }
};

/**
 * Run `task` over `items`, at most `limit` at a time, keeping result order.
 * Stops taking new items once `signal` aborts.
 */
const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>,
  signal?: AbortSignal
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length && !signal?.aborted) {
      const index = next++;
      results[index] = await task(items[index]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
  return results;
};

/**
 * Hook for fetching directions from Mapbox Directions API
 *
 * Mapbox accepts a limited number of coordinates per request, so longer routes
 * are fetched in overlapping windows and stitched into one result. A window
 * Mapbox can't route is drawn as a straight line (see `RouteLeg.isApproximate`)
 * and left out of the distance and duration totals.
 *
 * @example
 * ```tsx
 * const { fetchDirections, isReady } = useRouteDirections();
 *
 * const handleGetDirections = async () => {
 *   if (!isReady) return;
 *   const directions = await fetchDirections(stops);
 *   if (directions) {
 *     console.log('Total duration:', directions.totalDurationText);
 *   }
 * };
 * ```
 */
const useRouteDirections = (): UseRouteDirectionsReturn => {
  const isReady = true;

  /**
   * Fetch directions for the given stops using Mapbox Directions API
   */
  const fetchDirections = useCallback(
    async (
      stops: EnrichedRouteStop[],
      options: FetchDirectionsOptions = {}
    ): Promise<RouteDirections | null> => {
      if (stops.length < 2) {
        console.warn('[useRouteDirections] Need at least 2 stops');
        return null;
      }

      const hasValidCoords = stops.every(
        (s) =>
          typeof s.lat === 'number' &&
          typeof s.lng === 'number' &&
          !isNaN(s.lat) &&
          !isNaN(s.lng)
      );
      if (!hasValidCoords) {
        console.error(
          '[useRouteDirections] Invalid coordinates in stops:',
          stops
        );
        return null;
      }

      const accessToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
      if (!accessToken) {
        console.error(
          '[useRouteDirections] No Mapbox access token configured'
        );
        return null;
      }

      const { travelMode = 'DRIVING', signal } = options;
      const profile = getMapboxProfile(travelMode);

      // Sort stops by position
      const sortedStops = [...stops].sort((a, b) => a.position - b.position);

      const windows = await mapWithConcurrency(
        chunkWaypoints(sortedStops),
        WINDOW_CONCURRENCY,
        async (windowStops) =>
          (await fetchWindow(windowStops, profile, accessToken, signal)) ??
          buildApproximateWindow(windowStops),
        signal
      );

      // Aborted: skipped windows are holes in `windows`, so there's no result
      if (signal?.aborted) return null;

      // Nothing routed at all: report failure rather than a straight-line route
      if (windows.every((window) => window.isApproximate)) return null;

      const legs = windows.flatMap((window) => window.legs);
      // Each window after the first starts at the previous window's last stop.
      // Drop that repeated point only when both ends landed on the same
      // coordinate: Mapbox snaps routed geometry to roads, while an unrouted
      // window keeps the raw stop, so the two can differ.
      const polylinePoints: LatLng[] = [];
      for (const window of windows) {
        const last = polylinePoints[polylinePoints.length - 1];
        const [first, ...rest] = window.polylinePoints;
        const repeatsLast =
          last !== undefined && first?.lat === last.lat && first?.lng === last.lng;
        polylinePoints.push(...(repeatsLast ? rest : window.polylinePoints));
      }
      const totalDistanceMeters = windows.reduce(
        (sum, window) => sum + window.distanceMeters,
        0
      );
      const totalDurationSeconds = windows.reduce(
        (sum, window) => sum + window.durationSeconds,
        0
      );

      // Calculate bounds from polyline points
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;
      for (const point of polylinePoints) {
        if (point.lat < minLat) minLat = point.lat;
        if (point.lat > maxLat) maxLat = point.lat;
        if (point.lng < minLng) minLng = point.lng;
        if (point.lng > maxLng) maxLng = point.lng;
      }

      const bounds =
        polylinePoints.length > 0
          ? {
              northeast: { lat: maxLat, lng: maxLng },
              southwest: { lat: minLat, lng: minLng },
            }
          : undefined;

      return {
        legs,
        polylinePoints,
        totalDistanceMeters,
        totalDistanceText: formatDistance(totalDistanceMeters),
        totalDurationSeconds,
        totalDurationText: formatDuration(totalDurationSeconds),
        bounds,
      };
    },
    []
  );

  return {
    fetchDirections,
    isReady,
  };
};

export default useRouteDirections;
